/**
 * src/v2/services/spatialRegistryService.ts
 * GeoSpatialRegistry V2 frontend service — the Spatial Object tier of
 * Country → Organization → Spatial Object → Child Spatial Object.
 *
 * Mirrors organizationService.ts: reads go through the backend's
 * read-only simulation, and every write follows build → sign → submit,
 * with the operator's own wallet signing the unsigned XDR the backend
 * returns.
 *
 * On identity: an object's on-chain id is the raw bytes of its GeoLink
 * UUID. Both forms are accepted everywhere here and both are returned, so
 * a caller holding a GeoLink row never has to convert anything to address
 * the chain. Nothing in GeoLink changes to support this.
 *
 * On enumeration: like the organization registry, this contract is a
 * key/value store with no index — it cannot list objects. GeoLink already
 * enumerates spatial objects, and it remains the place to do that. This
 * service answers "what does the chain say about *this* object", which is
 * the question GeoLink cannot answer.
 *
 * That split is also why `mirrorObjectOrganization` lives here: the chain
 * owns the binding, but a binding GeoLink has never heard of cannot be
 * listed from the organization side, so the two are written together.
 */

import { V2_API, resolveCountry, numericToAlpha2 } from '../config';
import { geoLinkSpatialObjectsAdminClient } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import { metadataOrganizationBridgeIsSafe } from '../../services/spatial/geoLinkCapabilities';

// ── Discriminants (mirror contracts/v2/geo-spatial-registry/src/lib.rs) ──────

export type ObjectStatusName = 'none' | 'active' | 'suspended' | 'retired';

export const OBJECT_STATUS: Record<ObjectStatusName, number> = {
  none: 0, active: 1, suspended: 2, retired: 3,
};

export const OBJECT_STATUS_NAME: Record<number, ObjectStatusName> = {
  0: 'none', 1: 'active', 2: 'suspended', 3: 'retired',
};

/**
 * Which statuses a given status may move to. Mirrors
 * `GeoSpatialRegistry::obj_transition_allowed`; duplicated here only to
 * grey out impossible buttons, never as the enforcement point.
 */
export const OBJECT_STATUS_TRANSITIONS: Record<ObjectStatusName, ObjectStatusName[]> = {
  none:      [],
  active:    ['suspended', 'retired'],
  suspended: ['active', 'retired'],
  retired:   [],
};

export type BindingStatusName = 'none' | 'proposed' | 'approved' | 'suspended' | 'removed';

export const BINDING_STATUS: Record<BindingStatusName, number> = {
  none: 0, proposed: 1, approved: 2, suspended: 3, removed: 4,
};

export const BINDING_STATUS_NAME: Record<number, BindingStatusName> = {
  0: 'none', 1: 'proposed', 2: 'approved', 3: 'suspended', 4: 'removed',
};

export type RelationshipName =
  | 'owner' | 'operator' | 'manager' | 'developer'
  | 'gov_authority' | 'venue_operator' | 'platform_managed';

export const RELATIONSHIP: Record<RelationshipName, number> = {
  owner: 1, operator: 2, manager: 3, developer: 4,
  gov_authority: 5, venue_operator: 6, platform_managed: 7,
};

export const RELATIONSHIP_LABEL: Record<RelationshipName, string> = {
  owner:            'Owner',
  operator:         'Operator',
  manager:          'Manager',
  developer:        'Developer',
  gov_authority:    'Government authority',
  venue_operator:   'Venue operator',
  platform_managed: 'Platform managed',
};

export type PermissionName = 'manage_object' | 'manage_rules' | 'manage_funds';

export const PERMISSION_BIT: Record<PermissionName, number> = {
  manage_object: 0x01, manage_rules: 0x02, manage_funds: 0x04,
};

/** The registry walks at most this many objects up the parent chain. */
export const MAX_HIERARCHY_DEPTH = 5;

// ── Types ────────────────────────────────────────────────────────────────────

export interface ObjectBinding {
  organizationId: string | null;
  relationship: number;
  relationshipName: RelationshipName | null;
  status: number;
  statusName: BindingStatusName;
  proposedBy: string | null;
  proposedAt: number | null;
  assignedBy: string | null;
  assignedAt: number | null;
  reasonHash: string | null;
}

export interface PendingTransfer {
  fromOrg: string | null;
  toOrg: string | null;
  proposedBy: string | null;
  proposedAt: number | null;
  reasonHash: string | null;
}

export interface SpatialObjectRecord {
  objectId: string;         // 32-char hex
  objectUuid: string | null; // dashed UUID, as GeoLink stores it
  registeredBy: string | null;
  registeredAt: number | null;
  updatedAt: number | null;
  metadataHash: string | null;
  numericCode: number | null;
  alpha2: string | null;
  status: number;
  statusName: ObjectStatusName;
  parentId: string | null;
  parentUuid: string | null;
  binding: ObjectBinding | null;
  /**
   * The organization the object answers to, which may come from an
   * ancestor. Null when nothing in the chain is approved.
   */
  effectiveOrganizationId: string | null;
  /** True when the effective organization came from a parent, not this object. */
  inherited: boolean;
  beneficiary: string | null;
  canReceive: boolean;
  pendingTransfer: PendingTransfer | null;
}

export interface PermissionGrant {
  mask: number;
  manageObject: boolean;
  manageRules: boolean;
  manageFunds: boolean;
}

export interface ObjectAuthority {
  objectId: string;
  objectUuid: string | null;
  wallet: string;
  /** Effective — folds in the organization's clearance and the whole chain's status. */
  canManageObject: boolean;
  canManageRules: boolean;
  /**
   * The raw object-level grant. Can be non-zero while the effective
   * answers are false; that is how a suspended parent or a withdrawn
   * organization authority shows up.
   */
  grant: PermissionGrant;
}

export interface SpatialRegistryConfig {
  configured: boolean;
  contractId: string | null;
}

export interface BuiltSpatialTx {
  xdr: string;
  latestLedger: number;
  objectId: string;
  objectUuid: string | null;
}

export interface SpatialSubmitResult {
  hash: string;
  ledger?: number;
}

// ── HTTP plumbing ────────────────────────────────────────────────────────────

const base = (path = ''): string => `${V2_API}/spatial-bindings${path}`;

async function asJson<T>(resp: Response): Promise<T> {
  let body: unknown = null;
  try { body = await resp.json(); } catch { /* empty or non-JSON body */ }

  if (!resp.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new Error(err?.error || err?.message || `Request failed: ${resp.status}`);
  }
  return body as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(base(path), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  return asJson<T>(resp);
}

// ── Object ids ───────────────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX16_RE = /^[0-9a-f]{32}$/i;

/** Normalize a GeoLink object id to 32 lower-case hex characters. */
export function normalizeObjectId(value: unknown): string | null {
  const raw = String(value ?? '').trim();
  if (UUID_RE.test(raw)) return raw.replace(/-/g, '').toLowerCase();
  if (HEX16_RE.test(raw)) return raw.toLowerCase();
  return null;
}

export function isObjectId(value: unknown): value is string {
  return normalizeObjectId(value) !== null;
}

/** 32 hex characters → the dashed UUID GeoLink uses. */
export function objectIdToUuid(hex: string): string | null {
  const h = String(hex || '').toLowerCase();
  if (!HEX16_RE.test(h)) return null;
  return [
    h.slice(0, 8), h.slice(8, 12), h.slice(12, 16), h.slice(16, 20), h.slice(20),
  ].join('-');
}

/** Short display form: first 8 hex characters. */
export function shortObjectId(objectId: string): string {
  return (normalizeObjectId(objectId) ?? objectId).slice(0, 8);
}

// ── Reads ────────────────────────────────────────────────────────────────────

export async function getSpatialRegistryConfig(): Promise<SpatialRegistryConfig> {
  try {
    const resp = await fetch(base('/config'));
    if (!resp.ok) return { configured: false, contractId: null };
    const data = await resp.json();
    return { configured: !!data.configured, contractId: data.contractId ?? null };
  } catch {
    // Backend unreachable is indistinguishable from not deployed for the
    // purpose of deciding whether to render the binding UI.
    return { configured: false, contractId: null };
  }
}

/**
 * The chain's record for one object. `null` when the object has not been
 * registered on chain — which is the normal state for an object that
 * exists only in GeoLink, not an error.
 */
export async function getSpatialObject(objectId: string): Promise<SpatialObjectRecord | null> {
  const id = normalizeObjectId(objectId);
  if (!id) return null;
  const resp = await fetch(base(`/${id}`));
  if (!resp.ok) return null;
  return resp.json();
}

export async function getObjectAuthority(
  objectId: string,
  wallet: string,
): Promise<ObjectAuthority | null> {
  const id = normalizeObjectId(objectId);
  if (!id || !wallet) return null;
  const resp = await fetch(base(`/${id}/authority/${wallet}`));
  if (!resp.ok) return null;
  return resp.json();
}

/** Matches BATCH_MAX in server/v2/routes/spatialBindings.js. */
const BATCH_MAX = 50;

/**
 * Resolve several objects at once, skipping any the chain does not know.
 *
 * Used to decorate a GeoLink-sourced list with on-chain ownership. Most
 * objects in a typical list are not registered on chain, so a miss is
 * expected and quiet.
 *
 * Goes through `/batch`, which reads them server-side: reading one object
 * costs nine contract simulations, so doing a list of them one HTTP call at
 * a time from the browser is slow in a way the user feels. Falls back to
 * per-id reads if the route is absent, which is what a server running an
 * older build will do.
 */
export async function getSpatialObjects(
  objectIds: string[],
): Promise<Map<string, SpatialObjectRecord>> {
  const ids = Array.from(new Set(objectIds.map(normalizeObjectId).filter((x): x is string => !!x)));
  const out = new Map<string, SpatialObjectRecord>();
  if (!ids.length) return out;

  for (let i = 0; i < ids.length; i += BATCH_MAX) {
    const chunk = ids.slice(i, i + BATCH_MAX);
    let records: SpatialObjectRecord[] | null = null;

    try {
      const data = await post<{ records?: SpatialObjectRecord[] }>('/batch', { objectIds: chunk });
      records = data.records ?? [];
    } catch {
      records = null; // fall through to the per-id path
    }

    if (records === null) {
      const settled = await Promise.all(chunk.map((id) => getSpatialObject(id)));
      records = settled.filter((r): r is SpatialObjectRecord => !!r);
    }

    for (const record of records) {
      if (record?.objectId) out.set(record.objectId, record);
    }
  }
  return out;
}

// ── Writes (build unsigned XDR) ──────────────────────────────────────────────

export async function buildRegisterObject(params: {
  caller: string;
  /** The GeoLink UUID, dashed or bare hex. */
  objectId: string;
  country: string | number;
  parentId?: string | null;
  /** Optional 64-hex hash of off-chain metadata. */
  metadataHash?: string | null;
}): Promise<BuiltSpatialTx & { numericCode: number; alpha2: string | null }> {
  const result = await post<BuiltSpatialTx & { numericCode: number; alpha2: string | null }>(
    '/objects',
    {
      caller:       params.caller,
      objectId:     params.objectId,
      country:      resolveCountry(params.country),
      parentId:     params.parentId ?? null,
      metadataHash: params.metadataHash ?? null,
    },
  );
  return { ...result, alpha2: result.alpha2 ?? numericToAlpha2(result.numericCode) };
}

export async function buildSetObjectStatus(
  objectId: string,
  caller: string,
  status: ObjectStatusName,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/status`, { caller, status });
}

/** Pass `null` to detach the object and make it a root. */
export async function buildSetObjectParent(
  objectId: string,
  caller: string,
  parentId: string | null,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/parent`, { caller, parentId });
}

/**
 * Lodge a claim that an organization stands in some relationship to an
 * object.
 *
 * Open to any wallet, and it **grants nothing** — the claim confers no
 * authority until a country admin approves it. Say so in the UI; an
 * operator who signs this and expects to be able to act on the object
 * afterwards has misread the model.
 */
export async function buildProposeBinding(params: {
  objectId: string;
  caller: string;
  organizationId: string;
  relationship?: RelationshipName;
  reasonHash?: string | null;
}): Promise<BuiltSpatialTx & { grantsAuthority: false }> {
  return post(`/${normalizeObjectId(params.objectId)}/binding`, {
    caller:         params.caller,
    organizationId: params.organizationId,
    relationship:   params.relationship ?? 'owner',
    reasonHash:     params.reasonHash ?? null,
  });
}

/**
 * Accept a proposed (or reinstate a suspended) binding. This is the write
 * that actually confers authority, and it requires country-admin scope
 * over the object plus an organization cleared to own objects in the same
 * country.
 */
export async function buildApproveBinding(
  objectId: string,
  caller: string,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/approve`, { caller });
}

export async function buildSuspendBinding(
  objectId: string,
  caller: string,
  reasonHash?: string | null,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/suspend`, {
    caller, reasonHash: reasonHash ?? null,
  });
}

/** Dissolves the binding and clears every operator and the beneficiary. */
export async function buildRemoveBinding(
  objectId: string,
  caller: string,
  reasonHash?: string | null,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/remove`, {
    caller, reasonHash: reasonHash ?? null,
  });
}

export async function buildSetRelationship(
  objectId: string,
  caller: string,
  relationship: RelationshipName,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/relationship`, {
    caller, relationship,
  });
}

/**
 * Step one of a reassignment. Completing it clears every object operator
 * and the beneficiary — warn the operator before they sign step two, not
 * after.
 */
export async function buildProposeReassign(
  objectId: string,
  caller: string,
  toOrganizationId: string,
  reasonHash?: string | null,
): Promise<BuiltSpatialTx & { clearsDelegatedRightsOnCompletion: true }> {
  return post(`/${normalizeObjectId(objectId)}/reassign`, {
    caller, toOrganizationId, reasonHash: reasonHash ?? null,
  });
}

export async function buildCompleteReassign(
  objectId: string,
  caller: string,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/reassign/complete`, { caller });
}

export async function buildCancelReassign(
  objectId: string,
  caller: string,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/reassign/cancel`, { caller });
}

/** Pass an empty array to revoke the wallet's grant entirely. */
export async function buildSetObjectOperator(
  objectId: string,
  caller: string,
  wallet: string,
  permissions: PermissionName[],
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/operators`, {
    caller, wallet, permissions,
  });
}

/** Pass `null` to clear the beneficiary. */
export async function buildSetObjectBeneficiary(
  objectId: string,
  caller: string,
  wallet: string | null,
): Promise<BuiltSpatialTx> {
  return post<BuiltSpatialTx>(`/${normalizeObjectId(objectId)}/beneficiary`, { caller, wallet });
}

// ── Submit ───────────────────────────────────────────────────────────────────

export async function submitSignedSpatialTx(signedXdr: string): Promise<SpatialSubmitResult> {
  const data = await post<{ success: true; hash: string; ledger?: number }>('/submit', { signedXdr });
  return { hash: data.hash, ledger: data.ledger };
}

/** Minimal shape of the wallet wrappers used across the app. */
export interface SpatialSigner {
  signTransaction(xdr: string, networkPassphrase?: string): Promise<string>;
}

/** build → sign → submit, the sequence every spatial write follows. */
export async function signAndSubmitSpatialTx(
  built: { xdr: string },
  signer: SpatialSigner,
  networkPassphrase?: string,
): Promise<SpatialSubmitResult> {
  const signedXdr = await signer.signTransaction(built.xdr, networkPassphrase);
  return submitSignedSpatialTx(signedXdr);
}

// ── GeoLink mirror ───────────────────────────────────────────────────────────

export type MirrorOutcome = 'written' | 'unchanged' | 'skipped' | 'failed';

export interface MirrorResult {
  outcome: MirrorOutcome;
  /** Empty when there is nothing worth telling the operator. */
  reason: string;
}

/**
 * Copy an approved-or-proposed binding into GeoLink's
 * `metadata.organization_id`.
 *
 * The chain is authoritative for *who owns what*, but it has no index, so
 * it cannot answer "which objects does this organization hold". GeoLink
 * can, and only for objects whose mirror value is set. Writing the mirror
 * on every binding is therefore not bookkeeping — it is the only thing
 * that keeps a binding discoverable from the organization side.
 *
 * Never throws. A failed mirror leaves a real on-chain binding intact and
 * must not be reported as a failed binding; the caller surfaces the
 * returned reason as a warning instead.
 *
 * Gated on `metadataOrganizationBridgeIsSafe` for the reason documented in
 * geoLinkCapabilities: on a GeoLink build that promotes this key straight
 * into a foreign key with no fallback, one unmirrored organization rejects
 * the entire object write, geometry included.
 */
export async function mirrorObjectOrganization(
  objectId: string,
  organizationId: string | null,
): Promise<MirrorResult> {
  const id = normalizeObjectId(objectId);
  if (!id) return { outcome: 'failed', reason: 'Invalid object id' };

  const uuid = objectIdToUuid(id);
  if (!uuid) return { outcome: 'failed', reason: 'Invalid object id' };

  const orgId = (organizationId || '').trim().toLowerCase();

  try {
    if (!(await metadataOrganizationBridgeIsSafe())) {
      return {
        outcome: 'skipped',
        reason:
          'The binding is recorded on chain, but GeoLink was not updated: this '
          + 'deployment does not report a guarded organization bridge, and writing '
          + 'it there can reject the whole object. The object will not appear in '
          + 'this organization\'s list until GeoLink is upgraded.',
      };
    }

    const row = await geoLinkSpatialObjectsAdminClient.getObject(uuid);
    if (!row) {
      return {
        outcome: 'failed',
        reason: 'The binding is recorded on chain, but GeoLink has no object with this id.',
      };
    }

    const existing = row.metadata ?? {};
    const current = String((existing as { organization_id?: unknown }).organization_id ?? '')
      .trim()
      .toLowerCase();
    if (current === orgId) return { outcome: 'unchanged', reason: '' };

    // Merge rather than replace: metadata carries colour keys, interaction
    // types and tier data that a binding has no business dropping.
    const metadata: Record<string, unknown> = { ...existing };
    if (orgId) metadata.organization_id = orgId;
    else delete metadata.organization_id;

    await geoLinkSpatialObjectsAdminClient.updateObject(uuid, { metadata });
    return { outcome: 'written', reason: '' };
  } catch (err) {
    return {
      outcome: 'failed',
      reason:
        'The binding is recorded on chain, but GeoLink could not be updated: '
        + `${err instanceof Error ? err.message : String(err)}. The object may not `
        + 'appear in this organization\'s list until this is retried.',
    };
  }
}

// ── Display helpers ──────────────────────────────────────────────────────────

/**
 * A one-line description of who controls an object and on what basis.
 * Kept here so the several places that need it cannot drift.
 */
export function describeOwnership(record: SpatialObjectRecord | null): string {
  if (!record) return 'Not registered on chain';
  if (!record.effectiveOrganizationId) {
    const proposed = record.binding?.statusName === 'proposed';
    return proposed ? 'Claimed, awaiting approval' : 'No organization bound';
  }
  const org = record.effectiveOrganizationId.slice(0, 8);
  if (record.inherited) return `Inherited from parent — ${org}`;
  const rel = record.binding?.relationshipName;
  const label = rel ? RELATIONSHIP_LABEL[rel] : 'Bound';
  return `${label} — ${org}`;
}
